import React, { useCallback, useEffect, useState } from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { ANALYTICS_CHANNEL } from '../../common/constants';

import { FlyoutContent } from './flyout-content';
import { ObjectListFlyoutProps } from './types';

/**
 * Component that renders a list of items in a flyout menu with searching & filtering capabilities in the list.
 * Consumers can provide the items data via the useItems hook.
 */
export function ObjectListFlyout({
	label,
	icon,
	analyticsName,
	triggerRef,
	isDefaultOpen,
	isOpen,
	onOpenChange,
	isSearchable = true,
	searchPlaceholder,
	searchNoResultsElem,
	emptyElem,
	loadingElem,
	filterConfig,
	useItems,
	ItemComponent,
	actions,
	testId,
}: ObjectListFlyoutProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isFlyoutOpen, setIsFlyoutOpen] = useState(isDefaultOpen ?? false);

	useEffect(() => {
		if (isOpen !== undefined) {
			setIsFlyoutOpen(isOpen);
		}
	}, [isOpen]);

	useEffect(() => {
		onOpenChange?.(isFlyoutOpen);
	}, [isFlyoutOpen, onOpenChange]);

	const closeFlyout = useCallback(() => setIsFlyoutOpen(false), []);

	const toggleFlyout = useCallback(() => {
		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'globalNavigation',
			actionSubjectId: 'objectListFlyoutTrigger',
			attributes: {
				name: analyticsName,
				isExpanding: !isOpen,
				component: 'ObjectListFlyout',
			},
		}).fire(ANALYTICS_CHANNEL);
		setIsFlyoutOpen((prev) => !prev);
	}, [createAnalyticsEvent, analyticsName, isOpen]);

	return (
		<FlyoutMenuItem isOpen={isFlyoutOpen} onOpenChange={setIsFlyoutOpen}>
			<FlyoutMenuItemTrigger
				ref={triggerRef}
				elemBefore={icon}
				onClick={toggleFlyout}
				testId={testId}
			>
				{label}
			</FlyoutMenuItemTrigger>

			<FlyoutMenuItemContent onClose={closeFlyout} containerTestId={testId && `${testId}-popup`}>
				<FlyoutContent
					label={label}
					isSearchable={isSearchable}
					searchPlaceholder={searchPlaceholder}
					searchNoResultsElem={searchNoResultsElem}
					emptyElem={emptyElem}
					loadingElem={loadingElem}
					filterConfig={filterConfig}
					useItems={useItems}
					ItemComponent={ItemComponent}
					actions={actions}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
